import { FC, useContext, useEffect, useMemo, useState } from 'react'
import './calendarsRow.scss'
import { ConfigProvider, DatePicker, Popover } from 'antd'
import { Button } from 'fronton-react'
import locale from 'antd/lib/locale/ru_RU'
import 'moment/locale/ru'
import {
    AdditionalCalendar,
    DefaultDatesState,
    LocationDates,
    OrderDay,
    PropertyDates
} from 'pages/my-calendars/add-calendar'
import moment, { Moment } from 'moment'
import { UnavailableDay, WeekDay } from 'pages/my-calendars/add-calendar/interfaces'
import {
    AMERICAN_DATE_FORMAT,
    DAY_STRING_FORMAT,
    DELAY_BEFORE_AVAILABLE_DAYS,
    MAY_HOLIDAYS_KEY,
    NEW_YEAR_KEY,
    RUSSIAN_SHORT_DATE_FORMAT
} from 'pages/my-calendars/constants'
import { cloneDeep, isEmpty, uniqueId } from 'lodash'
import { DeleteOutlined } from '@ant-design/icons'
import { isCurrentDateUnavailable, onlyRmsOrderDate } from 'shared/utils'
import { prevDaysMap } from 'shared/consts'
import { SupplierCalendarsContext } from 'contexts/SupplierCalendarsContext'
import {
    getDisableFilteredDates,
    getFilteredApprovedDates,
    isApprovedFilteredDates
} from 'shared/utils/approvedCalendars'
import { LocationType } from '../../../enums'

interface Props {
    isInfo: boolean
    locationId?: number
    className?: string
    onCalendarChange: (
        date: Moment | null,
        property: keyof LocationDates,
        additionalId?: string
    ) => void
    onAddAdditional?: (additionalId: string) => void
    onDeleteAdditional?: (additionalId: string) => void
    isNeedToClear?: boolean
    mainData?: LocationDates
    disableDates?: {
        orderDays: OrderDay[]
        unavailableDays: UnavailableDay[]
        lastOrderDate: string
        firstOrderDate: string
    }
    deliveryType?: string
    defaultValues?: DefaultDatesState
    isMayOrNewYearHoliday?: {
        type: string
        status: boolean
    }
    isDisabled?: boolean
    calendarId?: string
    locationType?: string
}

export const CalendarsRow: FC<Props> = ({
    isInfo,
    locationId,
    className,
    onCalendarChange,
    isNeedToClear,
    mainData,
    disableDates,
    deliveryType,
    defaultValues,
    onAddAdditional,
    onDeleteAdditional,
    isMayOrNewYearHoliday,
    isDisabled = false,
    calendarId,
    locationType
}) => {
    const [lastOrderValue, setLastOrderValue] = useState<Moment | null>(null)
    const [firstOrderValue, setFirstOrderValue] = useState<Moment | null>(null)
    const [orderValue, setOrderValue] = useState<Moment | null>(defaultValues?.order || null)
    const [deliveryValue, setDeliveryValue] = useState<Moment | null>(null)
    const [mainState, setMainState] = useState<LocationDates | null>(null)
    const [additionalCalendars, setAdditionalCalendars] = useState<AdditionalCalendar[]>([])

    const [isFilledAdditionalCalendars, setIsFilledAdditionalCalendars] = useState<boolean>(false)
    const [isEmptyOrderValue, setIsEmptyOrderValue] = useState<boolean>(false)

    const isDisabledAddButton = useMemo(() => {
        const isFilledAdditionalCalendars = additionalCalendars.length >= 2
        const isEmptyOrderValue = isEmpty(orderValue)
        const isEmptyAdditionalOrderValue =
            additionalCalendars.length === 1 && isEmpty(additionalCalendars[0].orderDate)

        return (
            isDisabled ||
            isFilledAdditionalCalendars ||
            isEmptyOrderValue ||
            isEmptyAdditionalOrderValue
        )
    }, [isDisabled, additionalCalendars, orderValue])

    useEffect(() => {
        const isFilledAdditionalCalendars = additionalCalendars.length >= 2
        setIsFilledAdditionalCalendars(isFilledAdditionalCalendars)

        const isEmptyOrderValue = isEmpty(orderValue)
        const isEmptyAdditionalOrderValue =
            additionalCalendars.length === 1 && isEmpty(additionalCalendars[0].orderDate)
        setIsEmptyOrderValue(isEmptyOrderValue || isEmptyAdditionalOrderValue)
    }, [additionalCalendars, orderValue])

    const handleCalendarValueChange = (
        date: Moment | null,
        property: keyof LocationDates,
        setFunc?: (newDate: Moment | null) => void,
        additionalRowId?: string
    ) => {
        if (additionalRowId && additionalCalendars.length <= 2) {
            const copyArray = cloneDeep(additionalCalendars)
            const index = additionalCalendars.findIndex((item) => item.index === additionalRowId)

            if (index !== -1 && (property === 'orderDate' || property === 'deliveryDate')) {
                copyArray[index][property] = date
                setAdditionalCalendars(copyArray)
            }
        } else if (setFunc) {
            setFunc(date)
        }
        onCalendarChange(date, property, additionalRowId)
    }

    useEffect(() => {
        if (isNeedToClear) {
            setFirstOrderValue(null)
            setOrderValue(null)
            setLastOrderValue(null)
            setDeliveryValue(null)
            setAdditionalCalendars(
                additionalCalendars.map((item) => ({
                    ...item,
                    orderDate: null,
                    deliveryDate: null
                }))
            )
        }
    }, [isNeedToClear])

    useEffect(() => {
        if (defaultValues) {
            setLastOrderValue(
                defaultValues.lastOrder &&
                    (isInfo || !isLastOrderDateDisabled(defaultValues.lastOrder))
                    ? defaultValues.lastOrder
                    : null
            )
            setFirstOrderValue(
                defaultValues.firstOrder &&
                    (isInfo || !isFirstOrderDateDisabled(defaultValues.firstOrder))
                    ? defaultValues.firstOrder
                    : null
            )
            setDeliveryValue(
                defaultValues.delivery &&
                    (isInfo || !isDeliveryDateDisabled(defaultValues.delivery))
                    ? defaultValues.delivery
                    : null
            )
            setOrderValue(
                defaultValues.order &&
                    (isInfo || !isUnavailableDateDisabled(defaultValues.order, ''))
                    ? defaultValues.order
                    : null
            )
            setAdditionalCalendars(
                defaultValues.additionalFields.map((el) => {
                    const updatedDeliveryDate =
                        el.deliveryDate && (isInfo || !isDeliveryDateDisabled(el.deliveryDate))
                            ? el.deliveryDate
                            : el.deliveryDate

                    const updatedOrderDate =
                        el.orderDate &&
                        (isInfo || !isUnavailableDateDisabled(el.orderDate, el.index))
                            ? el.orderDate
                            : el.orderDate
                    return {
                        ...el,
                        deliveryDate: updatedDeliveryDate,
                        orderDate: updatedOrderDate
                    }
                })
            )
            setMainState({
                id: mainData ? mainData.id : '',
                firstOrderDate: mainData ? mainData.firstOrderDate : null,
                lastOrderDate: mainData ? mainData.lastOrderDate : null,
                deliveryDate: mainData ? mainData.deliveryDate : null,
                orderDate: mainData ? mainData.orderDate : null
            })
        }
    }, [defaultValues, isInfo])
    const isMainStateValuesAreDifferent = (
        mainStateProperty: keyof PropertyDates,
        mainDataValue: Date | null,
        disableDatesProperty: 'unavailableDays' | 'orderDays'
    ) =>
        mainDataValue &&
        mainState &&
        mainState[mainStateProperty]?.getTime() !== mainDataValue.getTime() &&
        disableDates?.[disableDatesProperty].findIndex(
            //eslint-disable-next-line @typescript-eslint/no-explicit-any
            (it: any) =>
                it[mainStateProperty === 'orderDate' ? 'weekDay' : 'orderDay'].toLowerCase() ===
                moment(mainDataValue).locale('en').format(DAY_STRING_FORMAT).toLowerCase()
        ) === -1

    useEffect(() => {
        const datesObject: { [key in keyof PropertyDates]: Date | null } = {
            firstOrderDate: mainData ? mainData.firstOrderDate : null,
            lastOrderDate: mainData ? mainData.lastOrderDate : null,
            orderDate: mainData ? mainData.orderDate : null,
            deliveryDate: mainData ? mainData.deliveryDate : null
        }

        Object.keys(datesObject).forEach((key) => {
            if (
                key === 'firstOrderDate' ||
                key === 'lastOrderDate' ||
                key === 'orderDate' ||
                key === 'deliveryDate'
            ) {
                if (
                    mainState &&
                    isMainStateValuesAreDifferent(
                        key,
                        datesObject[key],
                        key === 'orderDate' ? 'unavailableDays' : 'orderDays'
                    )
                ) {
                    setMainState({
                        ...mainState,
                        [key]: datesObject[key]
                    })
                }
            }
        })
    }, [
        mainState,
        mainData?.orderDate,
        mainData?.deliveryDate,
        mainData?.firstOrderDate,
        mainData?.lastOrderDate
    ])

    const isMayOrNewYear = (currentDate: Moment) => {
        let isUnavailablePeriod = false

        if (isMayOrNewYearHoliday && isMayOrNewYearHoliday.status) {
            if (isMayOrNewYearHoliday.type.includes(NEW_YEAR_KEY)) {
                isUnavailablePeriod =
                    currentDate.month() !== 10 &&
                    currentDate.month() !== 11 &&
                    currentDate.month() !== 0
            } else if (isMayOrNewYearHoliday.type.includes(MAY_HOLIDAYS_KEY)) {
                isUnavailablePeriod = currentDate.month() !== 3 && currentDate.month() !== 4
            }
        }

        return isUnavailablePeriod
    }

    const prevDays: Array<WeekDay> = (disableDates?.orderDays || []).map(
        (i) => prevDaysMap[i.orderDay]
    )

    // RFS240725_3925: CDCalendar поправить логику с пересечением дат
    const { approvedData, worksheetData } = useContext(SupplierCalendarsContext)

    const { filteredApprovedDates } = useMemo(() => {
        return getFilteredApprovedDates(approvedData, worksheetData, locationId, calendarId)
    }, [approvedData, worksheetData, locationId])

    const { disableFilteredDates } = useMemo(() => {
        return getDisableFilteredDates(filteredApprovedDates)
    }, [filteredApprovedDates])

    const isLastOrderDateDisabled = (currentDate: Moment) => {
        const isMay = isMayOrNewYear(currentDate)
        const isCDU = isCurrentDateUnavailable(currentDate, 'orderDays', disableDates)
        const isRMS = onlyRmsOrderDate(currentDate, prevDays, disableDates)
        const isSameDates =
            currentDate &&
            !!firstOrderValue &&
            moment(currentDate).isSameOrAfter(firstOrderValue, 'day')
        const isTooEarlyToOrder =
            currentDate && currentDate < moment().add(DELAY_BEFORE_AVAILABLE_DAYS, 'days')

        // RFS251202_4815: отключить функционал проверки наличия дней недоступности магазинов
        /*const isBetweenLastAndFirstOrderDates =
            currentDate &&
            moment(disableDates?.lastOrderDate) < currentDate &&
            currentDate < moment(disableDates?.firstOrderDate)*/

        let isApprovedDates = false

        if (locationType === LocationType.Store) {
            const result = isApprovedFilteredDates(disableFilteredDates, currentDate)
            isApprovedDates = result.isApprovedDates
        }

        return isMay || isCDU || isRMS || isSameDates || isTooEarlyToOrder || isApprovedDates
    }

    const isFirstOrderDateDisabled = (currentDate: Moment) => {
        const isMay = isMayOrNewYear(currentDate)
        const isCDU = isCurrentDateUnavailable(currentDate, 'orderDays', disableDates)
        const isRMS = onlyRmsOrderDate(currentDate, prevDays, disableDates)
        const isSameDates =
            currentDate &&
            !!lastOrderValue &&
            moment(currentDate).isSameOrBefore(lastOrderValue, 'day')
        const isMoreThan36DaysAfterLastOrder =
            currentDate &&
            !!lastOrderValue &&
            currentDate >= moment(lastOrderValue.format(AMERICAN_DATE_FORMAT)).add(36, 'days')

        // RFS251202_4815: отключить функционал проверки наличия дней недоступности магазинов
        /*const isBetweenLastAndFirstOrderDates =
            currentDate &&
            moment(disableDates?.lastOrderDate) < currentDate &&
            currentDate < moment(disableDates?.firstOrderDate)*/

        let isApprovedDates = false

        if (locationType === LocationType.Store) {
            const result = isApprovedFilteredDates(disableFilteredDates, currentDate)
            isApprovedDates = result.isApprovedDates
        }

        return (
            isMay ||
            isCDU ||
            isRMS ||
            isSameDates ||
            isMoreThan36DaysAfterLastOrder ||
            isApprovedDates
        )
    }

    const isDeliveryDateDisabled = (currentDate: Moment) => {
        const isMay = isMayOrNewYear(currentDate)
        const isCDU = isCurrentDateUnavailable(currentDate, 'orderDays', disableDates)
        const isRMS =
            deliveryType === 'BBXD' ? onlyRmsOrderDate(currentDate, prevDays, disableDates) : ''
        const isSameAsLastOrderDate =
            lastOrderValue && currentDate.diff(lastOrderValue, 'day') === 0 // deliveryDate can't be lastOrderDate CDCALENDAR-51
        const isOutsideValidOrderInterval =
            currentDate &&
            !!firstOrderValue &&
            !!lastOrderValue &&
            (currentDate >= firstOrderValue || currentDate <= lastOrderValue)

        let isApprovedDates = false

        if (locationType === LocationType.Store) {
            const result = isApprovedFilteredDates(disableFilteredDates, currentDate)
            isApprovedDates = result.isApprovedDates
        }

        // Получаем массив всех deliveryDate из additionalCalendars
        const additionalDeliveryDates = additionalCalendars
            .map((calendar) => calendar.deliveryDate)
            .filter(Boolean) // Исключаем undefined или null

        // Проверяем, совпадает ли currentDate с любой датой из additionalDeliveryDates
        const isInAdditionalCalendars = additionalDeliveryDates.some((deliveryDate) =>
            currentDate.isSame(moment(deliveryDate), 'day')
        )

        if (orderValue?.isSame(currentDate, 'day')) {
            return true
        }

        return (
            isMay ||
            isCDU ||
            isRMS ||
            isSameAsLastOrderDate ||
            isOutsideValidOrderInterval ||
            isApprovedDates ||
            isInAdditionalCalendars
        )
    }

    const isUnavailableDateDisabled = (currentDate: Moment, additionalId: string) => {
        const additionalItem = additionalCalendars.find((el) => el.index === additionalId)

        const deliveryDateValue = additionalItem?.deliveryDate || deliveryValue

        // Если текущая дата совпадает с orderDate, она должна быть доступна
        if (additionalItem?.orderDate?.isSame(currentDate, 'day')) {
            return false
        }

        if (!additionalId) {
            if (deliveryValue?.isSame(currentDate, 'day')) {
                return true
            }
        }

        const isMay = isMayOrNewYear(currentDate)
        const isCDU = isCurrentDateUnavailable(currentDate, 'unavailableDays', disableDates)
        const isBeforeDeliveryDate =
            currentDate && deliveryDateValue !== null && currentDate <= deliveryDateValue
        const isMoreThanOneMonthAfterDeliveryDate =
            currentDate &&
            !!deliveryDateValue &&
            currentDate >= moment(deliveryDateValue.format(AMERICAN_DATE_FORMAT)).add(1, 'month')

        let isApprovedDates = false

        if (locationType === LocationType.Store) {
            const result = isApprovedFilteredDates(disableFilteredDates, currentDate)
            isApprovedDates = result.isApprovedDates
        }

        let isAddedOrderDates = false

        // Проверка совпадения deliveryDate первого и второго объектов
        if (
            additionalCalendars[1]?.deliveryDate?.isSame(
                additionalCalendars[0]?.deliveryDate,
                'day'
            )
        ) {
            // Обновляем состояние, обнуляя deliveryDate первого объекта
            setAdditionalCalendars((prevCalendars) => {
                const updatedCalendars = [...prevCalendars]
                updatedCalendars.map((item) => {
                    return {
                        ...item,
                        deliveryDate: null
                    }
                })
                return updatedCalendars
            })
        }

        if (additionalId) {
            if (additionalItem?.deliveryDate?.isSame(currentDate, 'day')) {
                return true
            }

            const allOrderDates = [
                orderValue,
                ...additionalCalendars.map((item) => item.orderDate?.startOf('day'))
            ]
            isAddedOrderDates = allOrderDates
                .filter(Boolean)
                .some((item) => item?.isSame(currentDate, 'day'))
        }

        return (
            isMay ||
            isCDU ||
            isBeforeDeliveryDate ||
            isMoreThanOneMonthAfterDeliveryDate ||
            isApprovedDates ||
            isAddedOrderDates
        )
    }

    const isAddDeliveryDateDisabled = (currentDate: Moment, additionalId: string) => {
        const additionalItem = additionalCalendars.find((el) => el.index === additionalId)
        const copyArray = additionalCalendars.reduce(
            (prev: AdditionalCalendar[], curr: AdditionalCalendar) => {
                if (curr.deliveryDate) {
                    return [...prev, curr]
                }
                return prev
            },
            []
        )

        if (
            additionalCalendars[0]?.deliveryDate?.isSame(
                additionalCalendars[1]?.deliveryDate,
                'day'
            )
        ) {
            // Обновляем состояние, обнуляя deliveryDate первого объекта
            setAdditionalCalendars((prevCalendars) => {
                const updatedCalendars = [...prevCalendars]
                updatedCalendars[1] = {
                    ...updatedCalendars[1],
                    deliveryDate: null
                }
                return updatedCalendars
            })
        }

        const { deliveryDate } = copyArray[copyArray.length - 1] || {}
        const currentZeroHours = moment(currentDate.format(AMERICAN_DATE_FORMAT))
        const deliveryValueZeroHours = deliveryValue
            ? moment(deliveryValue.format(AMERICAN_DATE_FORMAT))
            : null
        const deliveryDateZeroHours = deliveryDate
            ? moment(deliveryDate.format(AMERICAN_DATE_FORMAT))
            : null

        let isApprovedDates = false

        if (locationType === LocationType.Store) {
            const result = isApprovedFilteredDates(disableFilteredDates, currentDate)
            isApprovedDates = result.isApprovedDates
        }

        if (moment(additionalItem?.orderDate)?.isSame(currentDate, 'day')) {
            return true
        }

        return (
            currentDate &&
            (isMayOrNewYear(currentDate) ||
                isCurrentDateUnavailable(currentDate, 'orderDays') ||
                (deliveryType === 'BBXD'
                    ? onlyRmsOrderDate(currentDate, prevDays, disableDates)
                    : '') ||
                (lastOrderValue && currentDate.diff(lastOrderValue, 'day') === 0) || // deliveryDate cant be lastOrderDate CDCALENDAR-51
                (currentDate &&
                    !!firstOrderValue &&
                    !!lastOrderValue &&
                    (currentDate >= firstOrderValue || currentDate <= lastOrderValue)) ||
                (currentDate && !!deliveryValue && currentDate <= deliveryValue) ||
                (!!deliveryValueZeroHours && currentZeroHours <= deliveryValueZeroHours) ||
                (!!deliveryDateZeroHours && currentZeroHours <= deliveryDateZeroHours) ||
                isApprovedDates)
        )
    }

    const handleAddBtn = () => {
        // Проверка, что в массиве additionalCalendars меньше двух элементов
        if (additionalCalendars.length <= 2) {
            const index = uniqueId() // Генерация уникального индекса
            setAdditionalCalendars([
                ...additionalCalendars,
                { index, id: '', orderDate: null, deliveryDate: null } // Добавление нового элемента
            ])

            if (onAddAdditional) {
                onAddAdditional(index) // Вызов колбэка, если он передан
            }
        }
    }

    const handleDeleteAdditionalRow = (id: string) => {
        setAdditionalCalendars(additionalCalendars.filter((item) => item.index !== id))
        if (onDeleteAdditional) {
            onDeleteAdditional(id)
        }
    }

    const getAddBtnPopoverText = () => {
        if (isFilledAdditionalCalendars) {
            return 'Можно добавить не более двух дополнительных пар дат доставки'
        } else if (isEmptyOrderValue) {
            return 'Заполните поле Дата доставки в дни недоступности, чтобы добавить дополнительные даты доставки'
        }

        return 'Нажмите чтобы добавить дополнительные даты доставки'
    }

    const copyArray = cloneDeep(additionalCalendars)
    return (
        <ConfigProvider locale={locale}>
            <div className={`calendars-content ${className ?? ''}`}>
                <div className="calendars-row">
                    <div className="calendars-pair">
                        <DatePicker
                            disabled={isDisabled || isInfo}
                            className="calendar"
                            placeholder="дд.мм.гг"
                            format={RUSSIAN_SHORT_DATE_FORMAT}
                            disabledDate={isLastOrderDateDisabled}
                            value={lastOrderValue}
                            onChange={(date) =>
                                handleCalendarValueChange(date, 'lastOrderDate', setLastOrderValue)
                            }
                        />
                        <span className="dice">-</span>
                        <DatePicker
                            disabled={isDisabled || isInfo || !lastOrderValue}
                            className="calendar"
                            placeholder="дд.мм.гг"
                            format={RUSSIAN_SHORT_DATE_FORMAT}
                            value={firstOrderValue}
                            disabledDate={isFirstOrderDateDisabled}
                            onChange={(date) =>
                                handleCalendarValueChange(
                                    date,
                                    'firstOrderDate',
                                    setFirstOrderValue
                                )
                            }
                        />
                    </div>
                    <div className="calendars-pair">
                        <DatePicker
                            disabled={isDisabled || isInfo || !firstOrderValue}
                            className="calendar"
                            placeholder="дд.мм.гг"
                            format={RUSSIAN_SHORT_DATE_FORMAT}
                            value={deliveryValue}
                            disabledDate={isDeliveryDateDisabled}
                            onChange={(date) =>
                                handleCalendarValueChange(date, 'deliveryDate', setDeliveryValue)
                            }
                        />
                        <span className="dice">-</span>
                        <DatePicker
                            disabled={isDisabled || isInfo || !deliveryValue}
                            className="calendar"
                            placeholder="дд.мм.гг"
                            format={RUSSIAN_SHORT_DATE_FORMAT}
                            value={orderValue}
                            disabledDate={(currDate) => isUnavailableDateDisabled(currDate, '')}
                            onChange={(date) =>
                                handleCalendarValueChange(date, 'orderDate', setOrderValue)
                            }
                        />
                    </div>
                    {!isInfo && (
                        <Popover
                            content={<span>{getAddBtnPopoverText()}</span>}
                            overlayClassName="add-btn-popover"
                        >
                            <Button
                                kind="regular"
                                size="s"
                                variant="secondary"
                                className={`add-btn ${isDisabledAddButton ? 'disabled' : ''}`}
                                onClick={isDisabledAddButton ? undefined : handleAddBtn}
                            >
                                +
                            </Button>
                        </Popover>
                    )}
                </div>
                {!!additionalCalendars.length &&
                    copyArray.slice(0, 2).map((item, index) => {
                        return (
                            <div className="calendars-row additionalFields-row" key={item.index}>
                                <div className="calendars-pair" />
                                <div className="calendars-pair">
                                    <DatePicker
                                        disabled={
                                            isDisabled ||
                                            isInfo ||
                                            !deliveryValue ||
                                            (index !== 0 &&
                                                !additionalCalendars[index - 1]?.deliveryDate)
                                        }
                                        className="calendar"
                                        placeholder="дд.мм.гг"
                                        format={RUSSIAN_SHORT_DATE_FORMAT}
                                        value={item.deliveryDate}
                                        disabledDate={(currDate) =>
                                            isAddDeliveryDateDisabled(currDate, item.index)
                                        }
                                        onChange={(date) =>
                                            handleCalendarValueChange(
                                                date,
                                                'deliveryDate',
                                                undefined,
                                                item.index
                                            )
                                        }
                                    />
                                    <span className="dice">-</span>
                                    <DatePicker
                                        disabled={
                                            isDisabled ||
                                            isInfo ||
                                            !item.deliveryDate ||
                                            (index !== 0 &&
                                                !additionalCalendars[index - 1]?.deliveryDate)
                                        }
                                        className="calendar"
                                        placeholder="дд.мм.гг"
                                        format={RUSSIAN_SHORT_DATE_FORMAT}
                                        value={item.orderDate}
                                        disabledDate={(currDate) =>
                                            isUnavailableDateDisabled(currDate, item.index)
                                        }
                                        onChange={(date) =>
                                            handleCalendarValueChange(
                                                date,
                                                'orderDate',
                                                undefined,
                                                item.index
                                            )
                                        }
                                    />
                                </div>
                                {!isInfo && (
                                    <DeleteOutlined
                                        className="delete-additionalFields-row"
                                        onClick={() => handleDeleteAdditionalRow(item.index)}
                                    />
                                )}
                            </div>
                        )
                    })}
            </div>
        </ConfigProvider>
    )
}
